/**
 * Token Manager - Handles token storage and refresh logic
 * Uses localStorage for persistence
 */

const TOKEN_KEY = 'neev_access_token';
const REFRESH_TOKEN_KEY = 'neev_refresh_token';
const TOKEN_EXPIRY_KEY = 'neev_token_expires_at';

class TokenManager {
  /**
   * Store tokens in localStorage
   * @param {string} accessToken - JWT access token
   * @param {string} refreshToken - Refresh token
   * @param {string|Date} expiresAt - Token expiry time (ISO string or Date)
   */
  setTokens(accessToken, refreshToken, expiresAt) {
    try {
      localStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      
      // Convert to ISO string if Date object
      const expiryString = expiresAt instanceof Date 
        ? expiresAt.toISOString() 
        : expiresAt;
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryString);
    } catch (error) {
      console.error('Failed to store tokens:', error);
      throw new Error('Failed to save login information');
    }
  }

  /**
   * Get access token from localStorage
   * @returns {string|null} Access token or null if not found
   */
  getAccessToken() {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch (error) {
      console.error('Failed to get access token:', error);
      return null;
    }
  }

  /**
   * Get refresh token from localStorage
   * @returns {string|null} Refresh token or null if not found
   */
  getRefreshToken() {
    try {
      return localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error('Failed to get refresh token:', error);
      return null;
    }
  }

  /**
   * Get token expiry time
   * @returns {Date|null} Expiry date or null if not found
   */
  getTokenExpiry() {
    try {
      const expiryString = localStorage.getItem(TOKEN_EXPIRY_KEY);
      if (!expiryString) return null;
      return new Date(expiryString);
    } catch (error) {
      console.error('Failed to get token expiry:', error);
      return null;
    }
  }

  /**
   * Check if token is expired or will expire soon
   * @param {number} bufferSeconds - Buffer time in seconds before expiry (default: 300 = 5 minutes)
   * @returns {boolean} True if token is expired or will expire soon
   */
  isTokenExpired(bufferSeconds = 300) {
    const expiry = this.getTokenExpiry();
    if (!expiry) return true;
    
    const now = new Date();
    const bufferTime = bufferSeconds * 1000;
    return expiry.getTime() - now.getTime() < bufferTime;
  }

  /**
   * Clear all tokens from localStorage
   */
  clearTokens() {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
    } catch (error) {
      console.error('Failed to clear tokens:', error);
    }
  }

  /**
   * Check if user has tokens stored
   * @returns {boolean} True if tokens exist
   */
  hasTokens() {
    return !!this.getAccessToken() && !!this.getRefreshToken();
  }
}

// Export singleton instance
export default new TokenManager();

